/* eslint-disable max-lines-per-function */
/* eslint-disable no-irregular-whitespace */
import { Icon20Check } from '@vkontakte/icons';
import { classNames, Spacing, Text } from '@vkontakte/vkui';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { type VK_MUSIC_BUNDLE_SOURCE, VK_MUSIC_RADAR_NAMES } from 'reactApp/modules/vkMusic/vkMusic.const';
import { type VKIDInfo, type VkMusicBundleTariffInfo, VkMusicBundleVkidSource } from 'reactApp/modules/vkMusic/vkMusic.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import { VkMusicBundleLicenseNote } from '../VkMusicBundleLicenseNote/VkMusicBundleLicenseNote';
import styles from './VkMusicBundleModal.css';
import { getVkMusicBundleModalCoverImageUrl } from './VkMusicBundleModal.helpers';

export interface VkMusicBundleModalProps {
    onShow?: () => void;
    onClick?: () => void;
    onCancel?: () => void;
    onClose?: () => void;
    tariffInfo: VkMusicBundleTariffInfo;
    vkid?: VKIDInfo;
    source: VK_MUSIC_BUNDLE_SOURCE;
}

const decoration = (
    <svg width="50" height="49" viewBox="0 0 50 49" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.9691 27.9119C22.0006 25.0398 28.8202 19.0027 32.7806 15.2485" strokeWidth="4" strokeLinecap="square" />
        <path d="M8.05915 18.5991L10.5082 7.49285" strokeWidth="4" strokeLinecap="square" />
        <path d="M24.8883 41.0769L36.1215 42.855" strokeWidth="4" strokeLinecap="square" />
    </svg>
);

export const VkMusicBundleModal = memo<VkMusicBundleModalProps>(({ onShow, onClick, onClose, onCancel, tariffInfo, vkid, source }) => {
    const isDarkTheme = isDarkThemeModeEnabled();
    const { space, isTrial, discount, usualPrice } = tariffInfo;

    const sendAnalytics = useCallback((action: string, restArgs = {}) => {
        sendPaymentGa({
            action,
            name: VK_MUSIC_RADAR_NAMES.OFFER_MODAL,
            eventCategory: ECategoryGa.modal,
            is_trial: isTrial,
            vkid_source: vkid?.vkidSource || VkMusicBundleVkidSource.noVkid,
            offer_source: source,
            ...restArgs,
        });
    }, []);

    useEffect(() => {
        sendAnalytics('show');
        emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_OFFER_SHOW, { isTrial, source });
        onShow?.();
    }, []);

    const handleOnPrimaryClick = useCallback(() => {
        sendAnalytics('click', { button_name: 'try' });
        emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_OFFER_CLICK, { isTrial, source });
        onClick?.();
    }, []);

    const handleOnSecondaryClick = useCallback(() => {
        sendAnalytics('click', { button_name: 'not_now' });
        emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_OFFER_CLOSE, { isTrial, source });
        onCancel?.();
    }, []);

    const handleOnCancel = useCallback(() => {
        sendAnalytics('close');
        emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_OFFER_CLOSE, { isTrial, source });
        onCancel?.();
    }, []);

    const coverImage = getVkMusicBundleModalCoverImageUrl(isDarkTheme);

    const header = useMemo(() => {
        if (isTrial) {
            return 'Музыка нас связала — Mail Space и VK Музыка теперь в одной подписке';
        }

        return `Mail Space и VK Музыка в одной подписке — со скидкой ${discount}%`;
    }, []);

    const featureList = useMemo(() => {
        if (isTrial) {
            return [
                'Два сервиса за 0 ₽ в первый месяц',
                `Безлимитное место в Облаке для фото с телефона, для остального ${space.value}`,
                'Треки и аудиокниги с выключенным экраном и без интернета',
                'И всё без рекламы',
            ];
        }
        return [
            `Безлимитное место в Облаке для фото с телефона, для остального ${space.value}`,
            'Треки и аудиокниги с выключенным экраном и без интернета',
            'И всё без рекламы',
        ];
    }, [isTrial, space]);

    const content = useMemo(() => {
        return (
            <ol data-qa-id="features" className={styles.featureList}>
                {featureList.map((value, index) => (
                    <li className={styles.feature} key={index}>
                        <Icon20Check className={styles.point} />
                        {value}
                    </li>
                ))}
            </ol>
        );
    }, []);

    const priceContainer = useMemo(() => {
        return (
            <div className={styles.priceContainer}>
                <div className={styles.decoration}>{decoration}</div>
                <Text className={styles.price}>+{space.value}</Text>
            </div>
        );
    }, []);

    const themeStyle = isDarkTheme ? styles.dark : styles.light;

    return (
        <WhatsNewDialog
            className={classNames(styles.root, themeStyle)}
            imageUrl={coverImage}
            imageWrapperClassName={styles.imageWrapper}
            imageClassName={styles.image}
            contentClassName={styles.content}
            title={header}
            content={
                <>
                    <Spacing size={16} />
                    {content}
                    <Spacing size={24} />
                </>
            }
            imageAdditionalContent={priceContainer}
            primaryButtonText={isTrial ? 'Попробовать за 0 ₽' : `Подключить за ${usualPrice} ₽`}
            secondaryButtonText="Не сейчас"
            onSecondary={handleOnSecondaryClick}
            closeBySecondaryButton
            onClick={handleOnPrimaryClick}
            onClose={onClose}
            onDismiss={handleOnCancel}
            imageBgColor="var(--vkui--color_background_modal)"
            buttonTheme="vk"
            dialogSize="large"
            qaId="vkmusic_bundle_enter_modal"
            isRebranding
            footerText={<VkMusicBundleLicenseNote />}
            footerTextBelowButtons
        />
    );
});

VkMusicBundleModal.displayName = 'VkMusicBundleModal';
